import { Button } from "@components/ui/Button"
import { Input } from "@components/ui/Input"
import { Modal } from "@components/ui/Modal"
import { Segmented } from "@components/ui/Segmented"
import { Select } from "@components/ui/Select"
import { useToast } from "@components/ui/Toaster"
import { useSubjects } from "@features/classrooms"
import { normalizeImage } from "@features/ocr"
import { logger } from "@lib/logger"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { useCreateReference } from "../hooks"

import { ReferenceDropzone } from "./ReferenceDropzone"
import { isPdf, withDeclaredMime } from "./referenceVisuals"

import {
  REFERENCE_FILE_MIME_ALLOWLIST,
  REFERENCE_IMAGE_MIME_ALLOWLIST,
  REFERENCE_MAX_FILE_BYTES,
  REFERENCE_MAX_IMAGE_BYTES,
  REFERENCE_MAX_PAGES,
  REFERENCE_MAX_TEXT_CHARS,
  REFERENCE_MAX_VLM_PAGES,
} from "@/constants/references"
import { apiErrorCode, apiErrorKeys } from "@/lib/apiError"
import type { ReferenceDocument } from "@/types"

type Kind = "file" | "image" | "text" | "url"
type Phase = "idle" | "preparing" | "uploading"

interface Props {
  open: boolean
  onClose: () => void
  onCreated?: (doc: ReferenceDocument) => void
}

const MB = 1024 * 1024
const HEIC = new Set(["image/heic", "image/heif"])
const FILE_MIMES = new Set<string>(REFERENCE_FILE_MIME_ALLOWLIST)
const IMAGE_MIMES = new Set<string>(REFERENCE_IMAGE_MIME_ALLOWLIST)
const NO_SUBJECT = ""

/** `https://` on a public host, mirroring the shape check the API makes
 *  (core/reference_fetch.py::validate_url); the worker does the rest. */
function parseHttpsUrl(raw: string): URL | null {
  try {
    const parsed = new URL(raw.trim())
    return parsed.protocol === "https:" && parsed.hostname.includes(".") ? parsed : null
  } catch {
    return null
  }
}

function titleFromUrl(parsed: URL): string {
  const tail = parsed.pathname
    .replace(/\/$/, "")
    .split("/")
    .pop()
    ?.replace(/\.(html?|php|aspx?|pdf)$/, "")
    .replace(/[-_]+/g, " ")
  const host = parsed.hostname.replace(/^www\./, "")
  return tail ? `${host} · ${tail}` : host
}

function titleFromFilename(name: string): string {
  return name
    .replace(/\.[A-Za-z0-9]{1,5}$/, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

export function AddReferenceModal({ open, onClose, onCreated }: Props) {
  const { t } = useTranslation(["references", "common"])
  const toast = useToast()
  const create = useCreateReference()
  const subjectsQuery = useSubjects()

  const [kind, setKind] = useState<Kind>("file")
  const [file, setFile] = useState<File | null>(null)
  const [text, setText] = useState("")
  const [url, setUrl] = useState("")
  const [title, setTitle] = useState("")
  const [titleTouched, setTitleTouched] = useState(false)
  const [subjectId, setSubjectId] = useState<string>(NO_SUBJECT)
  const [pageFrom, setPageFrom] = useState("")
  const [pageTo, setPageTo] = useState("")
  const [consent, setConsent] = useState(false)
  const [phase, setPhase] = useState<Phase>("idle")
  const [progress, setProgress] = useState<{ loaded: number; total: number } | null>(null)
  const busy = phase !== "idle"

  const subjectOptions = useMemo(
    () => [
      { value: NO_SUBJECT, label: t("references:add.subject_none") },
      ...(subjectsQuery.data ?? []).map((s) => ({ value: s.id, label: s.name })),
    ],
    [subjectsQuery.data, t],
  )

  const pdf = kind === "file" && isPdf(file)
  const from = pageFrom ? Number(pageFrom) : null
  const to = pageTo ? Number(pageTo) : null
  const pageRangeInvalid =
    pdf &&
    (from !== null || to !== null) &&
    (from === null ||
      from < 1 ||
      (to !== null && (to < from || to - from + 1 > REFERENCE_MAX_PAGES)))

  // The submit button says why it is blocked (PRD-59: disabled buttons say why).
  const blockedReason = useMemo<string | null>(() => {
    if (kind === "url") {
      if (!parseHttpsUrl(url)) return t("references:add.blocked.no_url")
    } else if (kind === "text") {
      if (text.trim().length < 3) return t("references:add.blocked.no_text")
      if (text.length > REFERENCE_MAX_TEXT_CHARS)
        return t("references:add.blocked.too_large", { max: REFERENCE_MAX_TEXT_CHARS })
    } else {
      if (!file) return t("references:add.blocked.no_file")
      const declared = withDeclaredMime(file).type
      const allowed =
        kind === "image"
          ? IMAGE_MIMES.has(declared) || HEIC.has(declared)
          : FILE_MIMES.has(declared)
      if (!allowed) return t("references:add.blocked.unsupported")
      const cap = kind === "image" ? REFERENCE_MAX_IMAGE_BYTES : REFERENCE_MAX_FILE_BYTES
      if (file.size > cap) return t("references:add.blocked.too_large", { max: cap / MB })
      if (pageRangeInvalid) return t("references:add.blocked.pages")
    }
    if (!consent) return t("references:add.blocked.consent")
    return null
  }, [kind, text, url, file, consent, pageRangeInvalid, t])

  function reset() {
    setKind("file")
    setFile(null)
    setText("")
    setUrl("")
    setTitle("")
    setTitleTouched(false)
    setSubjectId(NO_SUBJECT)
    setPageFrom("")
    setPageTo("")
    setConsent(false)
    setPhase("idle")
    setProgress(null)
  }

  function close() {
    if (busy) return
    reset()
    onClose()
  }

  function pickFile(picked: File) {
    setFile(picked)
    if (!titleTouched) setTitle(titleFromFilename(picked.name))
  }

  function changeUrl(next: string) {
    setUrl(next)
    const parsed = parseHttpsUrl(next)
    if (!titleTouched && parsed) setTitle(titleFromUrl(parsed))
  }

  function changeKind(next: Kind) {
    setKind(next)
    setFile(null)
    setPageFrom("")
    setPageTo("")
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (blockedReason || busy) return
    setPhase("preparing")
    try {
      let upload: File | undefined
      if (kind !== "text" && kind !== "url" && file) {
        upload = kind === "image" ? await normalizeImage(file) : withDeclaredMime(file)
        if (HEIC.has(upload.type)) {
          toast.error(t("references:add.heic_failed"))
          return
        }
      }
      setPhase("uploading")
      setProgress({ loaded: 0, total: upload?.size ?? text.length })
      const doc = await create.mutateAsync({
        payload: {
          kind,
          title: title.trim() || undefined,
          subject_id: subjectId || null,
          file: upload,
          text: kind === "text" ? text : undefined,
          url: kind === "url" ? url.trim() : undefined,
          page_from: pdf ? from : null,
          page_to: pdf ? (to ?? from) : null,
        },
        onProgress: (loaded, total) => setProgress({ loaded, total }),
      })
      toast.success(t("references:add.success"))
      logger.info("references.created", { document_id: doc.id, kind: doc.kind })
      reset()
      onCreated?.(doc)
      onClose()
    } catch (err) {
      toast.error(t(...apiErrorKeys(err)))
      logger.error("references.create.failed", { code: apiErrorCode(err), kind })
    } finally {
      setPhase("idle")
      setProgress(null)
    }
  }

  const percent = progress ? Math.round((progress.loaded / Math.max(1, progress.total)) * 100) : 0

  return (
    <Modal
      open={open}
      onClose={close}
      title={t("references:add.title")}
      size="lg"
      closeLabel={t("common:actions.close")}
      blockScrimClose={busy}
      footer={
        <div className="flex flex-wrap items-center justify-end gap-3">
          {blockedReason && !busy ? (
            <p className="text-meta w-full sm:mr-auto sm:w-auto">{blockedReason}</p>
          ) : null}
          {phase === "uploading" && progress ? (
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={percent}
              aria-label={t("references:add.uploading", { percent })}
              className="bg-inset h-2 w-full overflow-hidden rounded-full sm:mr-auto sm:w-40"
            >
              <div
                className="bg-accent h-full rounded-full transition-[width] duration-300 motion-reduce:transition-none"
                style={{ width: `${percent}%` }}
              />
            </div>
          ) : null}
          <Button type="button" variant="ghost" onClick={close} disabled={busy}>
            {t("common:actions.cancel")}
          </Button>
          <Button type="submit" form="add-reference-form" disabled={!!blockedReason} loading={busy}>
            {phase === "uploading"
              ? t("references:add.uploading", { percent })
              : t("references:add.submit")}
          </Button>
        </div>
      }
    >
      <form id="add-reference-form" onSubmit={submit} className="space-y-5">
        <Segmented<Kind>
          ariaLabel={t("references:add.kind_label")}
          value={kind}
          onChange={changeKind}
          options={[
            { value: "file", label: t("references:kind.file") },
            { value: "image", label: t("references:kind.image") },
            { value: "text", label: t("references:kind.text") },
            { value: "url", label: t("references:kind.url") },
          ]}
        />

        {kind === "url" ? (
          <Input
            name="reference-url"
            type="url"
            inputMode="url"
            autoComplete="off"
            data-autofocus
            label={t("references:add.url_label")}
            value={url}
            onChange={(e) => changeUrl(e.target.value)}
            maxLength={2048}
            disabled={busy}
            placeholder={t("references:add.url_placeholder")}
          />
        ) : kind === "text" ? (
          <div>
            <label htmlFor="reference-text" className="text-label mb-1 block">
              {t("references:add.text_label")}
            </label>
            <textarea
              id="reference-text"
              data-autofocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
              maxLength={REFERENCE_MAX_TEXT_CHARS}
              disabled={busy}
              placeholder={t("references:add.text_placeholder")}
              className="border-border bg-surface text-ink placeholder:text-ink-subtle rounded-control focus-visible:ring-accent w-full border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
            />
            <p className="text-meta mt-1 text-right" aria-live="polite">
              {t("references:add.text_counter", {
                count: text.length,
                max: REFERENCE_MAX_TEXT_CHARS,
              })}
            </p>
          </div>
        ) : (
          <ReferenceDropzone
            kind={kind}
            file={file}
            onPick={pickFile}
            onClear={() => setFile(null)}
            disabled={busy}
          />
        )}

        <Input
          name="reference-title"
          label={t("references:add.title_label")}
          value={title}
          onChange={(e) => {
            setTitle(e.target.value)
            setTitleTouched(true)
          }}
          maxLength={300}
          disabled={busy}
          placeholder={t("references:add.title_placeholder")}
        />

        <div>
          <label id="reference-subject-label" className="text-label mb-1 block">
            {t("references:add.subject_label")}
          </label>
          <Select<string>
            value={subjectId}
            options={subjectOptions}
            onChange={setSubjectId}
            ariaLabelledBy="reference-subject-label"
            disabled={busy || subjectsQuery.isLoading}
            className="w-full"
          />
        </div>

        {pdf ? (
          <fieldset>
            <legend className="text-label mb-1">{t("references:add.pages_label")}</legend>
            <div className="flex items-center gap-2">
              <label htmlFor="reference-page-from" className="text-ink-muted text-sm">
                {t("references:add.pages_from")}
              </label>
              <input
                id="reference-page-from"
                type="number"
                inputMode="numeric"
                min={1}
                value={pageFrom}
                onChange={(e) => setPageFrom(e.target.value)}
                disabled={busy}
                className="border-border bg-surface text-ink rounded-control h-11 w-24 border px-3 text-sm md:h-9"
              />
              <label htmlFor="reference-page-to" className="text-ink-muted text-sm">
                {t("references:add.pages_to")}
              </label>
              <input
                id="reference-page-to"
                type="number"
                inputMode="numeric"
                min={1}
                value={pageTo}
                onChange={(e) => setPageTo(e.target.value)}
                disabled={busy}
                className="border-border bg-surface text-ink rounded-control h-11 w-24 border px-3 text-sm md:h-9"
              />
            </div>
            <p className="text-meta mt-1">
              {t("references:add.pages_hint", {
                max: REFERENCE_MAX_PAGES,
                vlm: REFERENCE_MAX_VLM_PAGES,
              })}
            </p>
          </fieldset>
        ) : null}

        <div className="space-y-2">
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              disabled={busy}
              className="accent-accent mt-0.5 h-5 w-5 shrink-0 md:h-4 md:w-4"
            />
            <span className="text-ink text-sm">{t("references:add.consent")}</span>
          </label>
          <p className="text-meta">
            {t("references:add.privacy")}
            {kind === "image" ? ` ${t("references:add.privacy_image")}` : null}
            {kind === "url" ? ` ${t("references:add.privacy_url")}` : null}
          </p>
        </div>
      </form>
    </Modal>
  )
}
